<template>
  <div class="i-landing-cards">
    <div
      class="label"
      :class="{ vis: visible }"
    >
      Where are you right now?
    </div>
    <div
      class="list"
      :class="{ vis: visible }"
    >
      <div
        v-for="card in cards"
        :key="card.situation"
        class="item"
        :class="{
          spotlight: spotlightCard === card.situation && !picked,
          sel: picked === card.situation,
          dim: picked && picked !== card.situation,
          other: card.situation === 'other'
        }"
        @mouseenter="pauseSpotlight"
        @mouseleave="resumeSpotlight"
        @click="pick(card)"
      >
        <div class="item-text">
          <div class="item-label">{{ card.label }}</div>
          <div
            v-if="card.sub"
            class="item-sub"
          >
            {{ card.sub }}
          </div>
        </div>
        <div class="item-arrow">&#x2192;</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue"
import { useRouter } from "vue-router"

import { GlobalVariables } from "@/helpers/globalVariables"
import GtmService, { UserFlow } from "@/services/gtmService"

defineProps({ visible: Boolean })
const emit = defineEmits(["pick"])
const router = useRouter()

const cards = [
  {
    situation: "laid-off",
    label: "I was recently laid off",
    sub: "I want to land something better than what I had.",
    msg: "Building your upgrade strategy..."
  },
  {
    situation: "stalled",
    label: "I'm employed but stuck or burning out",
    sub: "I'm not advancing, or something needs to change.",
    msg: "Diagnosing your specific constraint..."
  },
  {
    situation: "accelerate",
    label: "I'm ready for a bigger role",
    sub: "I want to move up, move out, or make a larger move.",
    msg: "Building your acceleration plan..."
  },
  {
    situation: "other",
    label: "None of these — just show me the assessment",
    sub: null,
    msg: "Starting your assessment..."
  }
]

const spotlightCard = ref(null)
const picked = ref(null)
let spotPaused = false
let timers = []

function clearTimers() {
  timers.forEach(clearTimeout)
  timers = []
  spotlightCard.value = null
}

function runSweep() {
  if (spotPaused || picked.value) return
  clearTimers()
  const stagger = 380,
    hold = 700
  cards.forEach((card, i) => {
    timers.push(
      setTimeout(() => {
        if (!spotPaused && !picked.value) spotlightCard.value = card.situation
      }, i * stagger)
    )
    timers.push(
      setTimeout(
        () => {
          if (spotlightCard.value === card.situation) spotlightCard.value = null
        },
        i * stagger + hold
      )
    )
  })
  const totalDur = (cards.length - 1) * stagger + hold + 2800
  timers.push(setTimeout(runSweep, totalDur))
}

function pauseSpotlight() {
  spotPaused = true
  clearTimers()
}

const resumeSpotlight = () => {
  spotPaused = false
  timers.push(setTimeout(runSweep, 1200))
}

const pick = (card) => {
  if (picked.value) return
  spotPaused = true
  clearTimers()
  picked.value = card.situation === "other" ? "stalled" : card.situation
  emit("pick", card)
  GtmService.newQuizStartEvent(UserFlow.COACHING)

  setTimeout(() => {
    router.push({
      name: GlobalVariables.urls.coaching.children.questionnaire.routeName,
      query: { situation: picked.value }
    })
  }, 2200)
}

// LIFECYCLE HOOKS
onMounted(() => {
  timers.push(setTimeout(runSweep, 2400))
})

onUnmounted(() => {
  clearTimers()
})
</script>

<style lang="scss" scoped>
.i-landing-cards {
  .label {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: $slate;
    margin-bottom: 12px;
    opacity: 0;
    transition: opacity 0.4s ease 0.4s;

    &.vis {
      opacity: 1;
    }
  }

  .list {
    display: flex;
    flex-direction: column;
    gap: 9px;
    opacity: 0;
    transform: translateY(12px);
    transition: all 0.55s ease 0.46s;

    &.vis {
      opacity: 1;
      transform: translateY(0);
    }

    @include respond-to($breakpoint-sm) {
      gap: 10px;
    }
  }

  .item {
    background: #1d4ed80a;
    backdrop-filter: blur(12px);
    /* stylelint-disable */
    -webkit-backdrop-filter: blur(12px);
    /* stylelint-enable */
    border: 1px solid #1d4ed824;
    border-left: 4px solid $amber;
    border-radius: $radius;
    padding: 15px 18px 15px 15px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    box-shadow: 0 1px 3px #1d4ed80f;
    transition:
      background 0.35s cubic-bezier(0.4, 0, 0.2, 1),
      border-color 0.35s cubic-bezier(0.4, 0, 0.2, 1),
      border-left-color 0.35s cubic-bezier(0.4, 0, 0.2, 1),
      transform 0.35s cubic-bezier(0.4, 0, 0.2, 1),
      box-shadow 0.35s cubic-bezier(0.4, 0, 0.2, 1);

    @include respond-to($breakpoint-md) {
      padding: 15px 18px 15px 16px;
    }

    &:hover,
    &.spotlight {
      background: #1d4ed814;
      border-color: #1d4ed838;
      border-left-color: $amber-warm;
      transform: translateX(5px);
      box-shadow: 0 4px 12px #1d4ed81a;

      .item-arrow {
        color: $white;
        background: $amber;
        width: 28px;
        height: 28px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 1;
        transform: translateX(2px);
      }

      .item-label {
        color: $amber;
      }
    }

    &:active {
      transform: translateX(5px) scale(0.99);
    }

    &.sel {
      background: #f1f5f9e6;
      border-left-color: $amber-warm;
      transform: translateX(5px);
    }

    &.dim {
      opacity: 0.2;
      pointer-events: none;
      transform: none;
    }

    &.other {
      background: transparent;
      border: 1px dashed #1d4ed838;
      border-left: 1px dashed #1d4ed838;
      padding: 11px 18px;
      box-shadow: none;

      .item-label {
        font-size: 13px;
        font-weight: 500;
        color: #11182799;
        margin-bottom: 0;
      }
    }
  }

  .item-text {
    flex: 1;
    min-width: 0;
  }

  .item-label {
    font-family: $sans;
    font-size: clamp(14px, 3.5vw, 15px);
    font-weight: 600;
    color: #111827e6;
    line-height: 1.2;
    margin-bottom: 3px;
    transition: color 0.35s cubic-bezier(0.4, 0, 0.2, 1);

    @include respond-to($breakpoint-md) {
      font-size: clamp(14px, 1.4vw, 16px);
    }
  }

  .item-sub {
    font-size: 12px;
    color: $ink-60;
    line-height: 1.45;
  }

  .item-arrow {
    flex-shrink: 0;
    font-size: 16px;
    color: $slate;
    opacity: 0.7;
    transition:
      color 0.35s cubic-bezier(0.4, 0, 0.2, 1),
      opacity 0.35s cubic-bezier(0.4, 0, 0.2, 1),
      transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  }
}
</style>
